import { useCallback, useEffect, useState } from "react";

export interface HistoryEntry {
  id: string;
  title: string;
  cover: string;
  viewedAt: number;
}

const FAVORITE_KEY = "nrg:favorites";
const HISTORY_KEY = "nrg:history";

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function useFavorites() {
  const [ids, setIds] = useState<string[]>(() => readJSON<string[]>(FAVORITE_KEY, []));

  const toggle = useCallback((id: string) => {
    setIds((prev) => {
      const next = prev.includes(id) ? prev.filter((item) => item !== id) : [id, ...prev];
      window.localStorage.setItem(FAVORITE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return {
    ids,
    isFavorite: (id: string) => ids.includes(id),
    toggle,
  };
}

export function useBrowseHistory() {
  const [history, setHistory] = useState<HistoryEntry[]>(() => readJSON<HistoryEntry[]>(HISTORY_KEY, []));

  const pushEntry = useCallback((entry: Omit<HistoryEntry, "viewedAt">) => {
    setHistory((prev) => {
      const filtered = prev.filter((item) => item.id !== entry.id);
      const next = [{ ...entry, viewedAt: Date.now() }, ...filtered].slice(0, 8);
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    window.localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
  }, []);

  return { history, pushEntry, clear };
}

export function useScrollTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return visible;
}